import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Assignment as PlannerIcon,
  StickyNote2 as TodoIcon,
  EventNote as TimeOffIcon,
  Inventory as CycleCountIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

const StatBox = ({ label, value, icon, color = 'primary.main' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <Box sx={{ 
      mr: 1, 
      display: 'flex', 
      alignItems: 'center',
      color: color
    }}>
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography variant="body1" sx={{ ml: 1, fontWeight: 'medium' }}>
      {value}
    </Typography>
  </Box>
);

function DashboardApp() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/dashboard/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setData(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Dashboard</Typography>
      
      <Grid container spacing={3}>
        {/* Planner Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            borderTop: 3, 
            borderColor: 'primary.main' 
          }}>
            <CardHeader
              title="Planner"
              avatar={<PlannerIcon color="primary" />}
              action={
                <IconButton onClick={() => navigate('/planner')}>
                  <ArrowForwardIcon />
                </IconButton>
              }
            />
            <CardContent>
              <StatBox 
                label="Planned" 
                value={data?.planner.planned || 0}
                icon={<PlannerIcon />} 
              />
              <StatBox 
                label="In Progress" 
                value={data?.planner.inProgress || 0}
                icon={<ScheduleIcon />}
                color="info.main"
              />
              <StatBox 
                label="Completed" 
                value={data?.planner.completed || 0}
                icon={<CheckCircleIcon />}
                color="success.main"
              />
              {(data?.planner.overdue > 0 || data?.planner.dueSoon > 0) && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {data?.planner.overdue > 0 && (
                    <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon fontSize="small" />
                      {data.planner.overdue} tasks overdue
                    </Typography>
                  )}
                  {data?.planner.dueSoon > 0 && (
                    <Typography variant="body2" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon fontSize="small" />
                      {data.planner.dueSoon} tasks due soon
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Todo List Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            borderTop: 3, 
            borderColor: 'secondary.main' 
          }}>
            <CardHeader
              title="Todo List"
              avatar={<TodoIcon color="secondary" />}
              action={
                <IconButton onClick={() => navigate('/todo')}>
                  <ArrowForwardIcon />
                </IconButton>
              }
            />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Progress
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(data?.todos.completed / data?.todos.total) * 100 || 0}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {data?.todos.completed || 0} of {data?.todos.total || 0} completed
                </Typography>
              </Box>
              {(data?.todos.overdue > 0 || data?.todos.dueSoon > 0) && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  {data?.todos.overdue > 0 && (
                    <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon fontSize="small" />
                      {data.todos.overdue} items overdue
                    </Typography>
                  )}
                  {data?.todos.dueSoon > 0 && (
                    <Typography variant="body2" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon fontSize="small" />
                      {data.todos.dueSoon} items due soon
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Time Off Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            borderTop: 3, 
            borderColor: 'success.main' 
          }}>
            <CardHeader
              title="Time Off"
              avatar={<TimeOffIcon color="success" />}
              action={
                <IconButton onClick={() => navigate('/timeoff')}>
                  <ArrowForwardIcon />
                </IconButton>
              }
            />
            <CardContent>
              <StatBox 
                label="This Week" 
                value={data?.timeOff.thisWeek || 0}
                icon={<ScheduleIcon />}
                color="info.main"
              />
              {data?.timeOff.upcoming > 0 && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeOffIcon fontSize="small" />
                    {data.timeOff.upcoming} scheduled in next 30 days
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cycle Count Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            borderTop: 3, 
            borderColor: 'info.main' 
          }}>
            <CardHeader
              title="Cycle Count"
              avatar={<CycleCountIcon color="info" />}
              action={
                <IconButton onClick={() => navigate('/cyclecount')}>
                  <ArrowForwardIcon />
                </IconButton>
              }
            />
            <CardContent>
              <StatBox 
                label="Last 30 Days" 
                value={data?.cycleCounts.last30Days || 0}
                icon={<CycleCountIcon />}
                color="info.main"
              />
              <StatBox 
                label="In Progress" 
                value={data?.cycleCounts.inProgress || 0}
                icon={<ScheduleIcon />}
                color="warning.main"
              />
              <StatBox 
                label="Completed" 
                value={data?.cycleCounts.completed || 0}
                icon={<CheckCircleIcon />}
                color="success.main"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default DashboardApp; 